import { useEffect, useMemo, useState } from "preact/hooks";
import { fetchAnalysis } from "../api";
import { AppShell } from "../components/AppShell";
import type { AnalysisData, DashboardConversationRow } from "../types";
import {
  addSaoPauloCalendarDays,
  fmtCanonicalPt,
  fmtDateTime,
  fmtDisplayName,
  fmtPhoneDisplay,
  fmtSaoPauloDateInput,
} from "../format";
import { AlertIconBadge, Badge, PriorityBadge } from "../components/atoms/Badge";
import { Pagination, usePagination } from "../components/Pagination";
import { useDebouncedSearch } from "../hooks/useDebouncedSearch";

interface FiltersState {
  q: string;
  intent: string;
  classification: string;
  dateFrom: string;
  dateTo: string;
}

const DEFAULT_FILTER_OPTIONS = {
  intents: ["purchase", "rental_long", "rental_short", "listing", "info", "partnership"],
  classifications: ["hot", "warm", "cold"],
};

function buildDefaultFilters(): FiltersState {
  const today = fmtSaoPauloDateInput();
  return {
    q: "",
    intent: "",
    classification: "",
    dateFrom: addSaoPauloCalendarDays(today, -3),
    dateTo: today,
  };
}

function rowDate(row: DashboardConversationRow): number {
  const value = row.updated_at || row.created_at;
  if (!value) return 0;
  const n = new Date(value).getTime();
  return Number.isFinite(n) ? n : 0;
}

function matchesFilters(row: DashboardConversationRow, filters: FiltersState): boolean {
  const q = filters.q.trim().toLowerCase();
  if (q) {
    const haystack = [
      row.client_name,
      row.client_phone,
      fmtPhoneDisplay(row.client_phone),
      row.client_email,
    ].filter(Boolean).join(" ").toLowerCase();
    if (!haystack.includes(q)) return false;
  }
  if (filters.intent && row.intent !== filters.intent) return false;
  if (filters.classification && row.classification !== filters.classification) return false;
  const d = rowDate(row);
  if (filters.dateFrom) {
    const from = new Date(`${filters.dateFrom}T00:00:00-03:00`).getTime();
    if (d < from) return false;
  }
  if (filters.dateTo) {
    const to = new Date(`${filters.dateTo}T23:59:59-03:00`).getTime();
    if (d > to) return false;
  }
  return true;
}

export function AnalysisPage({ mock }: { mock: boolean }) {
  const [data, setData] = useState<AnalysisData | null>(null);
  const [filters, setFilters] = useState<FiltersState>(() => buildDefaultFilters());
  const { searchValue, setSearchValue, debouncedQuery } = useDebouncedSearch(filters.q);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const effectiveFilters = useMemo<FiltersState>(() => ({
    ...filters,
    q: debouncedQuery,
  }), [filters, debouncedQuery]);
  const filterKey = JSON.stringify(effectiveFilters);

  useEffect(() => {
    setPage(1);
  }, [filterKey]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchAnalysis(mock, mock ? {} : {
      q: effectiveFilters.q,
      intent: effectiveFilters.intent,
      classification: effectiveFilters.classification,
      date_from: effectiveFilters.dateFrom,
      date_to: effectiveFilters.dateTo,
      page,
      page_size: pageSize,
    })
      .then((d) => {
        if (!d || d.ok === false) setError(d?.error || "Análise indisponível.");
        else {
          setData(d);
          setSelectedId(d.conversations[0]?.id || null);
        }
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, [mock, filterKey, page]);

  const rows = useMemo(
    () => {
      const sourceRows = data?.conversations || [];
      return mock ? sourceRows.filter((row) => matchesFilters(row, effectiveFilters)) : sourceRows;
    },
    [data, filterKey, mock],
  );
  const localPagination = usePagination(rows, {
    pageSize,
    resetKey: filterKey,
  });
  const pageItems = mock ? localPagination.pageItems : rows;
  const serverTotal = data?.pagination?.total_items ?? rows.length;
  const serverPageCount = Math.max(1, data?.pagination?.page_count ?? 1);
  const serverRangeStart = serverTotal ? (page - 1) * pageSize + 1 : 0;
  const serverRangeEnd = Math.min(serverTotal, (page - 1) * pageSize + rows.length);
  const selected = pageItems.find((row) => row.id === selectedId)
    || pageItems[0]
    || null;
  const filterOptions = data?.filters || DEFAULT_FILTER_OPTIONS;
  const filtersHeader = data && !error
    ? (
      <section class="filter-bar filter-bar--header">
        <label class="field field--search">
          <span>Busca</span>
          <input
            value={searchValue}
            onInput={(e) => setSearchValue(e.currentTarget.value)}
            placeholder="Nome ou telefone"
          />
        </label>
        <label class="field">
          <span>Intent</span>
          <select
            value={filters.intent}
            onChange={(e) => setFilters({ ...filters, intent: e.currentTarget.value })}
          >
            <option value="">Todos</option>
            {filterOptions.intents.map((intent) => (
              <option value={intent} key={intent}>{fmtCanonicalPt(intent)}</option>
            ))}
          </select>
        </label>
        <label class="field">
          <span>Classificação</span>
          <select
            value={filters.classification}
            onChange={(e) => setFilters({ ...filters, classification: e.currentTarget.value })}
          >
            <option value="">Todas</option>
            {filterOptions.classifications.map((c) => <option value={c} key={c}>{c}</option>)}
          </select>
        </label>
        <label class="field">
          <span>De</span>
          <input
            type="date"
            value={filters.dateFrom}
            onInput={(e) => setFilters({ ...filters, dateFrom: e.currentTarget.value })}
          />
        </label>
        <label class="field">
          <span>Até</span>
          <input
            type="date"
            value={filters.dateTo}
            onInput={(e) => setFilters({ ...filters, dateTo: e.currentTarget.value })}
          />
        </label>
      </section>
    )
    : null;

  return (
    <AppShell
      active="analise"
      title="Análise"
      subtitle="Investigação técnica"
      mock={mock}
      inspector={<AnalysisInspector row={selected} mock={mock} />}
      headerExtra={filtersHeader}
    >
      {loading && !data && <div class="state-msg">Carregando análise…</div>}
      {error && !data && <div class="state-error">{error}</div>}
      {data && !error && (
        <>
          <section class="rows-panel">
            <div class="rows-panel-head">
              <span>{rows.length} conversas</span>
              {mock && <Badge variant="info">mock</Badge>}
            </div>
            <div class="rows-panel-scroll">
              {loading && <div class="state-msg">Atualizando análise…</div>}
              {pageItems.map((row) => (
                <AnalysisRow
                  key={row.id}
                  row={row}
                  selected={selected?.id === row.id}
                  onSelect={() => setSelectedId(row.id)}
                />
              ))}
            </div>
            <Pagination
              page={mock ? localPagination.page : page}
              pageCount={mock ? localPagination.pageCount : serverPageCount}
              rangeStart={mock ? localPagination.rangeStart : serverRangeStart}
              rangeEnd={mock ? localPagination.rangeEnd : serverRangeEnd}
              totalItems={mock ? localPagination.totalItems : serverTotal}
              onPageChange={mock ? localPagination.setPage : setPage}
            />
          </section>
        </>
      )}
    </AppShell>
  );
}

function AnalysisRow(
  { row, selected, onSelect }: {
    row: DashboardConversationRow;
    selected: boolean;
    onSelect: () => void;
  },
) {
  const selectFromRow = (event: Event) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest("a, button")) return;
    onSelect();
  };
  const selectFromKeyboard = (event: KeyboardEvent) => {
    if (event.currentTarget !== event.target) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onSelect();
  };

  return (
    <article
      class={`conv-row conv-row--selectable ${selected ? "is-selected" : ""}`}
      role="button"
      tabIndex={0}
      onClick={selectFromRow}
      onKeyDown={selectFromKeyboard}
      aria-pressed={selected ? "true" : "false"}
    >
      <div class="conv-row-main">
        <div class="conv-row-title">
          <button class="link-button" type="button" onClick={onSelect}>
            {fmtDisplayName(row.client_name)}
          </button>
          <span>{fmtPhoneDisplay(row.client_phone)}</span>
        </div>
        <div class="conv-row-meta">
          <span>{fmtCanonicalPt(row.intent || "")}</span>
          <span>{fmtDateTime(row.updated_at)}</span>
          <span>{row.msg_count || 0} msgs</span>
        </div>
      </div>
      <div class="conv-row-badges">
        <PriorityBadge priority={row.classification as never} />
        <AlertIconBadge count={row.alert_count} alerts={row.alerts} />
      </div>
    </article>
  );
}

function AnalysisInspector(
  { row, mock }: { row?: DashboardConversationRow | null; mock: boolean },
) {
  const suffix = mock ? "?mock=1" : "";
  if (!row) {
    return <div class="inspector-card"><div class="state-msg">Sem conversa selecionada.</div></div>;
  }
  return (
    <div class="inspector-stack">
      <section class="inspector-card">
        <h2>Contexto</h2>
        <div class="inspector-title">{fmtDisplayName(row.client_name)}</div>
        <div class="inspector-muted">{fmtPhoneDisplay(row.client_phone)}</div>
        <a class="btn inspector-button" href={`/conv/${row.id}${suffix}`}>Abrir conversa</a>
      </section>
      <section class="inspector-card">
        <h2>Alertas</h2>
        {row.alerts?.length
          ? row.alerts.map((alert) => (
            <div class="inspector-alert" key={`${alert.label}-${alert.detail}`}>
              <Badge variant={alert.level === "danger" ? "danger" : alert.level === "warn" ? "warn" : "info"}>
                {alert.label}
              </Badge>
              {alert.detail && <span>{alert.detail}</span>}
            </div>
          ))
          : <div class="inspector-muted">Sem alertas.</div>}
      </section>
      <section class="inspector-card">
        <h2>Debug</h2>
        <pre class="inspector-pre">{JSON.stringify(row.analysis || {}, null, 2)}</pre>
      </section>
    </div>
  );
}
